import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Fade,
  Card,
  CardHeader,
  CardContent,
  Chip,
  Divider,
  Tooltip,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  Button,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import SyncIcon from '@mui/icons-material/Sync';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import PendingOutlinedIcon from '@mui/icons-material/PendingOutlined';

import Title from '../../components/Title';
import SearchBar from '../../components/SearchBar';
import { ProfileAvatar } from '../../components/DynamicForm';

import { httpRequest } from '../../api/helper';
import { useNotification } from '../../context/NotificationContext';
import { useAccount } from '../../context/AccountContext';
import { humanReadableDate } from '../../common/AppUtils';
import { USER_ROLES } from '../../common/AppConstants';

const TITLE = 'Staff Attendance';
const API_URL = 'attendance/staff-attendance/';
const API_DATE_FORMAT = 'YYYY-MM-DD';

const STATUS_PRESENT = 'present';
const STATUS_ABSENT = 'absent';
const STATUS_ON_DUTY = 'on_duty';

// Mirrors StaffAttendance.AWAY_STATUS_LIST on the backend
const AWAY_STATUS_LIST = [STATUS_ABSENT, STATUS_ON_DUTY];

const STATUS_OPTIONS = [
  { value: STATUS_PRESENT, label: 'Present', color: 'success' },
  { value: STATUS_ABSENT, label: 'Absent', color: 'error' },
  { value: STATUS_ON_DUTY, label: 'On Duty', color: 'info' },
];

const NO_DATA_AVAILABLE_STRING = 'No staff found for this organization.';

const isAwayStatus = (value) => AWAY_STATUS_LIST.includes(value);

const staffLabel = (staff_detail) =>
  `${staff_detail?.code} - ${staff_detail?.first_name} ${staff_detail?.last_name}`;

export default function StaffAttendance() {
  const { addNotification } = useNotification();
  const { account } = useAccount();

  const isAdmin =
    account?.user?.current_role?.toLowerCase() === USER_ROLES.ADMIN;

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [rows, setRows] = useState([]);
  const [marked, setMarked] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDirty, setIsDirty] = useState(false);

  // Filtered data based on search
  const filteredRows = rows.filter(row_item =>
    staffLabel(row_item.staff_detail).toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Live counts, update immediately as an Admin edits status per staff, before Save
  const statusCounts = STATUS_OPTIONS.map(option => ({
    ...option,
    count: rows.filter(row_item => row_item.status === option.value).length,
  }));

  const refreshData = async (targetDate) => {
    const dateParam = (targetDate || selectedDate).format(API_DATE_FORMAT);
    setIsLoading(true);

    await httpRequest({
      method: 'GET',
      url: API_URL,
      data: { date: dateParam },
      onSuccess: (data) => {
        setRows(data.rows || []);
        setMarked(data.marked);
        setIsDirty(false);
      },
      onFailure: (data) => {
        setRows([]);
        setMarked(false);
        setIsDirty(false);
      },
      addNotification,
    });

    setIsLoading(false);
  };

  useEffect(() => {
    refreshData(selectedDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  const handleDateChange = (newValue) => {
    if (!newValue || !newValue.isValid()) {
      return;
    }
    setSelectedDate(newValue);
  };

  const handleStatusChange = (staffId, newStatus) => {
    if (!newStatus) {
      // Null when the active button is clicked again
      return;
    }

    setRows((prevRows) =>
      prevRows.map((row) =>
        row.staff === staffId
          ? {
              ...row,
              status: newStatus,
              // Cover only applies to someone away, and defaults to needed
              substitute_required: isAwayStatus(newStatus)
                ? isAwayStatus(row.status)
                  ? row.substitute_required
                  : true
                : false,
            }
          : row,
      ),
    );
    setIsDirty(true);
  };

  const handleSubstituteRequiredToggle = (staffId, checked) => {
    setRows((prevRows) =>
      prevRows.map((row) =>
        row.staff === staffId ? { ...row, substitute_required: checked } : row,
      ),
    );
    setIsDirty(true);
  };

  const handleSave = async () => {
    setIsSaving(true);

    const payload = {
      date: selectedDate.format(API_DATE_FORMAT),
      entries: rows.map((row) => ({
        staff: row.staff,
        status: row.status,
        substitute_required: row.substitute_required,
      })),
    };

    await httpRequest({
      method: marked ? 'PUT' : 'POST',
      url: API_URL,
      data: payload,
      onSuccess: () => {
        addNotification(
          marked ? 'Attendance updated' : 'Attendance saved',
          'success',
        );
        refreshData(selectedDate);
      },
      addNotification,
    });

    setIsSaving(false);
  };

  const MarkedTile = () => {
    return (
      <Card
        variant="outlined"
        sx={{
          borderRadius: 2,
          borderColor: marked ? 'success.light' : 'warning.light',
          bgcolor: marked ? 'rgba(40, 180, 99, 0.06)' : 'rgba(202, 111, 30, 0.06)',
        }}
      >
        <CardContent sx={{ py: 1.25, '&:last-child': { pb: 1.25 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            {marked ? (
              <TaskAltIcon color="success" sx={{ fontSize: 24 }} />
            ) : (
              <PendingOutlinedIcon color="warning" sx={{ fontSize: 24 }} />
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4, minWidth: 0 }}>
              {/* Row 1: date, then attendance status */}
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, flexWrap: 'wrap' }}>
                <Typography variant="caption" color="text.secondary">
                  {humanReadableDate(selectedDate.format(API_DATE_FORMAT), true)}
                </Typography>
                <Typography variant="caption" color="text.disabled">
                  •
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {marked ? 'Attendance Saved' : 'Attendance Pending'}
                </Typography>
              </Box>

              {/* Row 2: live per-status counts */}
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {statusCounts.map((option) => (
                  <Chip
                    key={option.value}
                    size="small"
                    label={`${option.count} ${option.label}`}
                    variant="outlined"
                    color={option.color}
                    sx={{ height: 18, fontSize: '0.7rem', '& .MuiChip-label': { px: 0.75 } }}
                  />
                ))}
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  };

  const AttendanceCard = ({ attendance_dict }) => {
    const is_away = isAwayStatus(attendance_dict.status);
    const substitute_list = attendance_dict.staff_substitute_list || [];

    const statusOption =
      STATUS_OPTIONS.find((item) => item.value === attendance_dict.status) ||
      STATUS_OPTIONS[0];

    return (
      <Card
        variant="outlined"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 2,
          transition: 'box-shadow 0.3s',
          '&:hover': { boxShadow: 3 },
        }}
      >
        <CardHeader
          avatar={<ProfileAvatar dataDict={attendance_dict.staff_detail} />}
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h6">
                {staffLabel(attendance_dict.staff_detail)}
              </Typography>
              {!isAdmin && (
                <Chip
                  size="small"
                  label={statusOption.label}
                  color={statusOption.color}
                  variant="outlined"
                  sx={{ maxHeight: 18, fontSize: '0.8rem' }}
                />
              )}
            </Box>
          }
          sx={{
            px: 2,
            py: 0,
            alignItems: 'center',
            '& .MuiCardHeader-action': { alignSelf: 'center' },
          }}
          slotProps={{ content: { sx: { py: 1.5 } } }}
        />
        <Divider />

        <CardContent sx={{ pt: 1.5, pb: 1, flexGrow: 1 }}>
          {/* One row on a wide screen, wraps on a narrow one */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              rowGap: 1,
              flexWrap: 'wrap',
            }}
          >
            {/* Status selector, read only for a Teacher viewing their own row */}
            {isAdmin && (
              <ToggleButtonGroup
                size="small"
                exclusive
                value={attendance_dict.status}
                onChange={(e, value) =>
                  handleStatusChange(attendance_dict.staff, value)
                }
              >
                {STATUS_OPTIONS.map((option) => (
                  <ToggleButton
                    key={option.value}
                    value={option.value}
                    sx={{
                      textTransform: 'none',
                      fontSize: '0.8rem',
                      py: 0.5,
                      px: 2,
                      '&.Mui-selected': {
                        color: `${option.color}.main`,
                        borderColor: `${option.color}.light`,
                        fontWeight: 600,
                      },
                    }}
                  >
                    {option.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            )}

            {/* Allot Substitute only applies to someone who is away */}
            {isAdmin && is_away && (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography component="span" variant="body2" fontWeight="bold">
                  Allot Substitute:
                </Typography>
                <Switch
                  size="small"
                  checked={Boolean(attendance_dict.substitute_required)}
                  onChange={(e) =>
                    handleSubstituteRequiredToggle(
                      attendance_dict.staff,
                      e.target.checked,
                    )
                  }
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: 'success.light',
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: 'success.light',
                    },
                  }}
                />
              </Box>
            )}

            {isAdmin && is_away && attendance_dict.id && (
              <Typography variant="body2">
                <Typography component="span" variant="body2" fontWeight="bold">
                  Substitutes:{' '}
                </Typography>
                {attendance_dict.substitute_required
                  ? substitute_list.filter((item) => item.substitute).length
                  : 'Not required'}
              </Typography>
            )}

          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 64px - 64px)', // viewport height - margings - app bar
      }}
    >
      {/* Title and controls (fixed) */}
      <Box sx={{ flexShrink: 0, display: 'flex', flexDirection: 'column' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mt: 1,
            mb: 2,
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Title>{TITLE}</Title>
          </Box>

          <SearchBar searchTerm={searchTerm} setSearchTerm={setSearchTerm} />

          <Box
            sx={{
              flex: 1,
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                value={selectedDate}
                onChange={handleDateChange}
                format="DD MMM YYYY"
                slotProps={{
                  textField: { size: 'small', sx: { width: 170 } },
                }}
              />
            </LocalizationProvider>

            <Tooltip title="Refresh" arrow>
              <span>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => refreshData(selectedDate)}
                  disabled={isLoading}
                  sx={{ border: '1px solid rgba(0,0,0,0.1)', borderRadius: 1 }}
                >
                  <SyncIcon
                    fontSize="small"
                    sx={{
                      animation: isLoading ? 'spin 1s linear infinite' : 'none',
                      '@keyframes spin': {
                        '0%': { transform: 'rotate(0deg)' },
                        '100%': { transform: 'rotate(-360deg)' },
                      },
                    }}
                  />
                </IconButton>
              </span>
            </Tooltip>

            {isAdmin && (
              <Button
                variant="contained"
                startIcon={<CheckCircleIcon />}
                onClick={handleSave}
                disabled={
                  isLoading || isSaving || rows.length === 0 || (marked && !isDirty)
                }
                sx={{ px: 3 }}
              >
                {marked ? 'Update' : 'Save'}
              </Button>
            )}
          </Box>
        </Box>

        {/* Marked / Not marked tile */}
        <Box sx={{ mb: 2 }}>
          <MarkedTile />
        </Box>
      </Box>

      {/* Scrollable list container */}
      <Box
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          minHeight: 200,
          display: 'flex',
          flexDirection: 'column',
          alignItems: isLoading ? 'center' : 'stretch',
          justifyContent: isLoading ? 'center' : 'flex-start',
        }}
      >
        {isLoading ? (
          <Fade in={isLoading} timeout={500}>
            <CircularProgress size={50} thickness={3} />
          </Fade>
        ) : (
          <Fade in={!isLoading} timeout={500}>
            {rows.length === 0 ? (
              <Box sx={{ textAlign: 'left' }}>
                <Typography variant="h5">{NO_DATA_AVAILABLE_STRING}</Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  width: '100%',
                  py: 1,
                  display: 'grid',
                  // Single column on every breakpoint, the card reads as a list row
                  gridTemplateColumns: '1fr',
                  gap: 1.5,
                }}
              >
                {filteredRows.map((item_dict) => (
                  <Box key={item_dict.id || item_dict.staff}>
                    <AttendanceCard attendance_dict={item_dict} />
                  </Box>
                ))}
              </Box>
            )}
          </Fade>
        )}
      </Box>
    </Box>
  );
}
